import React from 'react'
import Badge from 'react-bootstrap/Badge'

/**
 * @typedef {object} AppBadgeOwnProps
 * @property {string} [variant]
 * @property {boolean} [pill]
 * @property {string} [className]
 * @property {React.ReactNode} [children]
 */

/**
 * Base badge component that maps variant names to the custom badge-* CSS classes.
 * Use this instead of manually setting `bg="" className="badge-{variant}"`.
 *
 * Supported variants: primary, secondary, success, danger, warning, info, high, ai, timeout
 *
 * The explicit annotation below is load-bearing: `React.forwardRef` with no type
 * argument infers its props as `{}`, so every `.tsx` caller passing `children`
 * failed to typecheck. The `Record<string, any>` half models the `...props`
 * spread onto the underlying Badge, which callers use for `style`, `text`,
 * `onClick` and `data-testid`.
 *
 * @type {React.ForwardRefExoticComponent<AppBadgeOwnProps & Record<string, any> & React.RefAttributes<HTMLElement>>}
 */
const AppBadge = React.forwardRef(({ variant, pill = false, className = '', children, ...props }, ref) => {
  const variantClass = variant ? `badge-${variant}` : ''

  return (
    <Badge
      ref={ref}
      bg=""
      pill={pill}
      className={`${variantClass} ${className}`.trim()}
      {...props}
    >
      {children}
    </Badge>
  )
})

export default AppBadge
